import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { SRSSelector } from './common';
import { MapProperties } from '@datum-map';

import proj4 from 'proj4';
import { get as getProjection } from 'ol/proj';
import { Services } from '@project/API';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { Icon, Style } from 'ol/style';
import image from '@assets/images/map-controls/place.svg';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { ZINDEX } from '@project/components/Map/constants';
import { Coordinate } from 'ol/coordinate';
import { Layer } from 'ol/layer';

export class CoordsTransform {
  @observable readonly selectors: SRSSelector;
  @observable private _error?: string = undefined;
  private _mapProperties: MapProperties;
  @observable lonInputValueFrom: number | undefined;
  @observable latInputValueFrom: number | undefined;
  @observable lonInputValueTo: number | undefined;
  @observable latInputValueTo: number | undefined;
  @observable fromProjection: string | undefined;
  @observable toProjection: string | undefined;
  private markerLayer: Layer | undefined;

  constructor(services: Services, mapProperties: MapProperties) {
    this._mapProperties = mapProperties;
    this.selectors = new SRSSelector(services);
    this._error = undefined;
    this.latInputValueFrom = undefined;
    this.lonInputValueFrom = undefined;
    this.latInputValueTo = undefined;
    this.lonInputValueTo = undefined;
    this.fromProjection = undefined;
    this.toProjection = undefined;
    this.markerLayer = undefined;
    makeObservable(this);
  }

  @action setLonInputValueFrom(value: number | undefined) {
    this.lonInputValueFrom = value;
  }

  @action setLatInputValueFrom(value: number | undefined) {
    this.latInputValueFrom = value;
  }

  @action setLatInputValueTo(value: number | undefined) {
    this.latInputValueTo = value;
  }

  @action setLonInputValueTo(value: number | undefined) {
    this.lonInputValueTo = value;
  }

  @action setFromProjection(value: string | undefined) {
    this.fromProjection = value;
  }

  @action setToProjection(value: string | undefined) {
    this.toProjection = value;
  }

  @action resetForm() {
    runInAction(() => {
      this.setLonInputValueFrom(undefined);
      this.setLatInputValueFrom(undefined);
      this.setFromProjection(undefined);
      this.setToProjection(undefined);
      this.setLatInputValueTo(undefined);
      this.setLonInputValueTo(undefined);
    });
  }

  @computed get error() {
    return this._error;
  }

  goToCoords = () => {
    runInAction(() => {
      this._error = undefined;
    });
    const isCoordsEntered =
      typeof this.lonInputValueFrom !== 'undefined' &&
      typeof this.latInputValueFrom !== 'undefined';

    const isFromProjectionSelected = typeof this.fromProjection !== 'undefined';

    if (isCoordsEntered && isFromProjectionSelected) {
      const mapProjection = this._mapProperties.map
        .getView()
        .getProjection()
        .getCode();

      if (typeof this.fromProjection === 'undefined') {
        return;
      }
      const fromProjection = getProjection(this.fromProjection);

      if (!fromProjection) return;

      const coordsProjection = fromProjection.getCode();

      if (
        typeof this.lonInputValueFrom === 'undefined' ||
        typeof this.latInputValueFrom === 'undefined'
      ) {
        return;
      }
      const coords = proj4(coordsProjection, mapProjection, [
        this.lonInputValueFrom,
        this.latInputValueFrom,
      ]);
      if (
        typeof coords[0] === 'undefined' ||
        typeof coords[1] === 'undefined'
      ) {
        return;
      }

      if (
        isNaN(coords[0]) ||
        isNaN(coords[1]) ||
        !isFinite(coords[0]) ||
        !isFinite(coords[1])
      ) {
        runInAction(() => {
          this._error = 'Не удалось перейти по координатам';
        });

        return;
      }

      this._mapProperties.map.getView().animate({
        center: coords,
        zoom: 18,
      });

      this.setMarker(coords);
    }
  };

  setMarker = (coords: Coordinate) => {
    this.removeMarker();

    const marker = new Feature({
      geometry: new Point(coords),
    });

    const icon = new Style({
      image: new Icon({
        anchor: [0.5, 40],
        anchorXUnits: 'fraction',
        anchorYUnits: 'pixels',
        src: image,
      }),
    });
    marker.setStyle(icon);

    const vectorSource = new VectorSource({
      features: [marker],
    });

    this.markerLayer = new VectorLayer({
      source: vectorSource,
    });

    this.markerLayer.setZIndex(ZINDEX.ROUTE_POINT);

    this._mapProperties.map.addLayer(this.markerLayer);
  };

  removeMarker = () => {
    if (this.markerLayer) {
      this._mapProperties.map.removeLayer(this.markerLayer);
    }
  };

  transformCoords = () => {
    runInAction(() => {
      this._error = undefined;
    });
    const lon = this.lonInputValueFrom;
    const lat = this.latInputValueFrom;

    if (
      typeof lon !== 'undefined' &&
      typeof lat !== 'undefined' &&
      typeof this.fromProjection !== 'undefined' &&
      typeof this.toProjection !== 'undefined'
    ) {
      const resultCoords = proj4(this.fromProjection, this.toProjection, [
        lon,
        lat,
      ]);
      if (
        typeof resultCoords[0] === 'undefined' ||
        typeof resultCoords[1] === 'undefined'
      ) {
        return;
      }
      if (
        isNaN(resultCoords[0]) ||
        isNaN(resultCoords[1]) ||
        !isFinite(resultCoords[0]) ||
        !isFinite(resultCoords[1])
      ) {
        runInAction(() => {
          this._error =
            'Не удалось перевести координаты в целевую систему координат';
        });

        return;
      }
      this.setLonInputValueTo(resultCoords[0]);
      this.setLatInputValueTo(resultCoords[1]);
    }
  };
}
